import { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { Navigate, useParams } from 'react-router-dom'
import { ErrorBoundary } from '@sentry/react'

import { ErrorFallbackContent } from 'components/ErrorFallbackContent'
import Loading from 'components/Loading'
import { SessionProvider } from 'context/Session'
import { useCompany } from 'utils/company'
import { extraGtmContainers, getCompanySlug, useFlow } from 'utils/flow'
import { showErrorPage } from 'utils/showErrorPage'
import ErrorContent from 'views/ErrorContent'

import Form from './Form'
import FormWizard from './FormWizard'
import FormWrapper from './FormWrapper'
import Layout from './Layout'

export default function FlowView(): JSX.Element {
  const { flowSlug } = useParams()
  const { data: extraGtmTags } = extraGtmContainers()
  const { data: flowData, isFetched, isError: isFlowError, error } = useFlow()
  const { isError: isCompanyError } = useCompany()

  // Effect to inject GTM scripts once data is available
  useEffect(() => {
    if (extraGtmTags && Array.isArray(extraGtmTags)) {
      extraGtmTags.forEach((tag: { container_id: string }, index) => {
        const { container_id: containerId } = tag

        // Create a dynamic dataLayer variable name
        const dataLayerName = `dataLayer${index}`

        // Initialize the dynamic dataLayer before injecting the GTM script
        ;(window as any)[dataLayerName] = (window as any)[dataLayerName] || []
        ;(window as any)[dataLayerName].push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js',
        })

        const script = document.createElement('script')
        script.async = true
        script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}&l=${dataLayerName}`

        // Append the script to the document head
        document.head.appendChild(script)

        // Manually trigger a custom dataLayer event to help Tag Assistant recognize the tag
        ;(window as any)[dataLayerName].push({
          event: `${dataLayerName}.init`, // Custom event name
          'event.trigger': `${dataLayerName}.manualInit`, // Custom trigger name
        })

        // Optionally: Add the noscript fallback as well
        const noScriptIframe = document.createElement('noscript')
        const iframe = document.createElement('iframe')
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`
        iframe.height = '0'
        iframe.width = '0'
        iframe.style.display = 'none'
        iframe.style.visibility = 'hidden'
        noScriptIframe.appendChild(iframe)
        document.body.appendChild(noScriptIframe)
      })
    }
  }, [extraGtmTags])

  // Show loading while verifying token
  if (!isFetched) {
    return <Loading />
  }

  if (isCompanyError) {
    return <ErrorContent title="Not Found" statusCode={404} text="Company not found" />
  }

  if (isFlowError) {
    const errorMap: Record<number, JSX.Element> = {
      401: <ErrorContent title="Unauthorized" statusCode={401} text="Unauthorized" />,
      403: <Navigate to="/login" replace state={{ from: flowSlug }} />,
      404: <ErrorContent title="Not Found" statusCode={404} text="Flow not found" />,
    }

    return showErrorPage(error, errorMap)
  }

  // Ensure flowData is available before rendering components that use useCurrentPage
  if (!flowData) {
    return <Loading />
  }

  return (
    <>
      <Helmet>
        <title>{flowData?.name ?? flowSlug}</title>
      </Helmet>
      <SessionProvider>
        <ErrorBoundary fallback={<ErrorFallbackContent />}>
          <FormWrapper>
            <Form>
              <Layout>
                <FormWizard />
              </Layout>
            </Form>
          </FormWrapper>
        </ErrorBoundary>
      </SessionProvider>
    </>
  )
}
